import {
  Users,
  Scale,
  SplitSquareVertical,
  Percent,
  UserCheck,
  Calculator,
  Settings2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FeatureLayout from '../../components/layout/FeatureLayout';
import SEOHead from '../../components/common/SEOHead';

const sharingModeKeys = [
  { icon: Scale, key: 'proportional', iconColor: 'pub-ico' },
  { icon: SplitSquareVertical, key: 'equal', iconColor: 'pub-ico' },
  { icon: Percent, key: 'custom', iconColor: 'pub-ico' },
  // Seul mode où la dépense repose sur UN partenaire : teinte partenaire B.
  { icon: UserCheck, key: 'singlePartner', iconColor: 'pub-ico--b' },
];

export default function FeatureSharing() {
  const { t } = useTranslation('features');

  return (
    <FeatureLayout
      breadcrumbs={[{ label: t('sharing.title') }]}
    >
      <SEOHead
        title="Partage des dépenses - Répartition équitable en couple"
        description="Partagez vos dépenses de couple équitablement : proportionnel aux revenus, 50/50, pourcentages personnalisés ou à la charge d'un seul partenaire."
        path="/fonctionnalites/partage"
      />
      {/* Hero */}
      <section className="pub-hero py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="pub-ico w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8" />
          </div>
          <h1 className="pub-title text-3xl sm:text-4xl font-bold mb-4">
            {t('sharing.title')}
          </h1>
          <p className="pub-muted text-lg max-w-2xl mx-auto">
            {t('sharing.subtitle')}
          </p>
        </div>
      </section>

      {/* Modes de partage */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="pub-title text-2xl font-bold mb-8 text-center">
            {t('sharing.modesTitle')}
          </h2>
          <div className="grid gap-6">
            {sharingModeKeys.map((mode) => (
              <div
                key={mode.key}
                className="pub-card p-6"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${mode.iconColor}`}>
                    <mode.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="pub-title text-lg font-semibold mb-2">
                      {t(`sharing.modes.${mode.key}.title`)}
                    </h3>
                    <p className="pub-muted mb-3">{t(`sharing.modes.${mode.key}.description`)}</p>
                    <div className="pub-example rounded-lg p-3 text-sm">
                      <span className="font-medium">{t('sharing.exampleLabel')}</span>
                      {t(`sharing.modes.${mode.key}.example`)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Exemple concret */}
      <section className="pub-section-alt py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="pub-ink rounded-2xl p-8 md:p-12">
            <h2 className="pub-title text-2xl md:text-3xl font-bold mb-6 text-center">
              {t('sharing.concreteExample')}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-lg mb-4 opacity-90">{t('sharing.monthlyIncome')}</h3>
                <ul className="space-y-2">
                  <li className="flex justify-between">
                    <span>Alice (salaire)</span>
                    <span className="font-semibold">3 000 €</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Bob (salaire)</span>
                    <span className="font-semibold">2 000 €</span>
                  </li>
                  <li className="flex justify-between pt-2 border-t border-white/20">
                    <span>{t('sharing.total')}</span>
                    <span className="font-semibold">5 000 €</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-4 opacity-90">{t('sharing.sharedExpenses')}</h3>
                <ul className="space-y-2">
                  <li className="flex justify-between">
                    <span>{t('sharing.alicePays60')}</span>
                    <span className="font-semibold">1 200 €</span>
                  </li>
                  <li className="flex justify-between">
                    <span>{t('sharing.bobPays40')}</span>
                    <span className="font-semibold">800 €</span>
                  </li>
                </ul>
                <p className="mt-4 text-sm opacity-75">
                  {t('sharing.fairShare')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flexibilité */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="pub-title text-2xl font-bold mb-8 text-center">
            {t('sharing.flexibilityTitle')}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="pub-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Calculator className="w-5 h-5 text-primary-600" />
                <h3 className="font-semibold">{t('sharing.autoCalc.title')}</h3>
              </div>
              <p className="pub-muted text-sm">
                {t('sharing.autoCalc.description')}
              </p>
            </div>
            <div className="pub-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Settings2 className="w-5 h-5 text-primary-600" />
                <h3 className="font-semibold">{t('sharing.coupleOrRoommate.title')}</h3>
              </div>
              <p className="pub-muted text-sm">
                {t('sharing.coupleOrRoommate.description')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </FeatureLayout>
  );
}
